import {Component} from "@angular/core";
import {ICellRendererAngularComp} from "ag-grid-angular";
import {ICellRendererParams} from "ag-grid-community";
import {NumberUtils} from "../../../../common/utils/number.utils";

/** What the External Forecast grid context gives the CT share cells (phase-2-edit-discard spec section 4 Q4). */
export interface CtTotalContext {
  /** The month's open-CT shares summed to 2 decimals, from the values on screen. */
  ctTotal(period: string): number;
  /** The month's CT split was held back because it does not total 100.00. */
  isCtMonthInvalid(period: string): boolean;
  /** Balance can fix the month: it has a CT the planner did not change and the changed shares leave room. */
  canBalanceCtMonth(period: string): boolean;
  /** Gives the CTs the planner did not change what the changed ones leave, so the month totals 100.00. */
  balanceCtMonth(period: string): void;
}

export type CtTotalIndicatorParams = ICellRendererParams & {context: CtTotalContext; locale: string};

/** Decimals of the total in the popover, as Staffing Distribution's. */
const TOTAL_DECIMALS = 2;

/**
 * CT % Distribution cell, as Staffing Distribution's validation-indicator-cell-v2: the formatted share, plus the error
 * icon on the focused cell of a month whose split is invalid; its popover gives the month's current total.
 */
@Component({
  standalone: false,
  selector: "esp-ct-total-indicator",
  templateUrl: "./ct-total-indicator.component.html",
  styleUrls: ["../../plan-distribution/validation-indicator-cell-v2/validation-indicator-cell-v2.component.css"]
})
export class CtTotalIndicatorComponent implements ICellRendererAngularComp {
  params: CtTotalIndicatorParams;

  agInit(params: CtTotalIndicatorParams): void {
    this.params = params;
  }

  /** The month total, locale-formatted; read when the popover opens, so it is the total on screen. */
  get total(): string {
    return NumberUtils.formatNumberIfNecessary(this.params.context.ctTotal(this.period), this.params.locale, TOTAL_DECIMALS);
  }

  get showIndicator(): boolean {
    const focused = this.params.api.getFocusedCell();
    return this.params.context.isCtMonthInvalid(this.period)
      && !!focused && focused.rowIndex === this.params.rowIndex && focused.column.getColId() === this.period;
  }

  get canBalance(): boolean {
    return this.params.context.canBalanceCtMonth(this.period);
  }

  balance(): void {
    this.params.context.balanceCtMonth(this.period);
  }

  /** Re-created on a forced refresh, as validation-indicator-cell-v2. */
  refresh(): boolean {
    return false;
  }

  private get period(): string {
    return this.params.column.getColId();
  }
}
